import React, { useState, useEffect } from "react"
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  LinearProgress,
  Skeleton,
  Alert,
  Button,
  Tooltip,
  Divider,
  Stack,
  CardHeader,
  CardActions,
  Collapse,
  ListItemSecondaryAction,
  useTheme,
  alpha,
} from "@mui/material"
import {
  Refresh as RefreshIcon,
  Group,
  Assignment,
  Schedule,
  Assessment,
  CalendarToday,
  Warning,
  CheckCircle,
  Error,
  Info,
  Timeline,
  TaskAlt,
  Person,
  ExpandMore,
  ExpandLess,
  AccountTree,
  Speed,
  AccountBalance,
  ArrowBack,
} from "@mui/icons-material"
import { useParams, useNavigate } from "react-router-dom"

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Utility functions
const formatCurrency = (amount) => {
  if (!amount || amount === 0) return "₹0"
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`
  return `₹${amount.toLocaleString()}`
}

const formatDate = (dateString) => {
  if (!dateString) return "Not set"
  return new Date(dateString).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

const calculateDaysRemaining = (endDate) => {
  if (!endDate) return null
  const end = new Date(endDate)
  const today = new Date()
  const diffTime = end - today
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

const getStatusColor = (status) => {
  const statusStr = String(status || "").toLowerCase()

  if (statusStr === "1") return "warning"
  if (statusStr === "2") return "primary"
  if (statusStr === "3") return "success"

  switch (statusStr) {
    case "completed":
    case "done":
    case "closed":
      return "success"
    case "testing":
    case "review":
      return "secondary"
    case "in progress":
    case "active":
    case "ongoing":
      return "primary"
    case "pending":
    case "todo":
    case "open":
      return "warning"
    case "cancelled":
    case "blocked":
      return "error"
    default:
      return "default"
  }
}

const getPriorityColor = (priority) => {
  const priorityStr = String(priority || "").toLowerCase()

  if (priorityStr === "1") return "#f44336"
  if (priorityStr === "2") return "#ff9800"
  if (priorityStr === "3") return "#4caf50"

  switch (priorityStr) {
    case "critical":
    case "urgent":
      return "#d32f2f"
    case "high":
      return "#f57c00"
    case "medium":
    case "normal":
      return "#1976d2"
    case "low":
      return "#388e3c"
    default:
      return "#757575"
  }
}

const getStatusDisplayName = (status) => {
  const statusStr = String(status || "").toLowerCase()

  if (statusStr === "1") return "To Do"
  if (statusStr === "2") return "In Progress"
  if (statusStr === "3") return "Done"

  return status || "Unknown"
}

const getPriorityDisplayName = (priority) => {
  const priorityStr = String(priority || "").toLowerCase()

  if (priorityStr === "1") return "High"
  if (priorityStr === "2") return "Medium"
  if (priorityStr === "3") return "Low"

  return priority || "Medium"
}

const getHealthStatus = (projectProgress, taskCompletionPercentage, daysRemaining) => {
  if (daysRemaining < 0) return { status: "Overdue", color: "error", icon: Error }

  if (projectProgress >= 90) return { status: "Excellent", color: "success", icon: CheckCircle }

  if (projectProgress >= 70) return { status: "Good", color: "success", icon: CheckCircle }

  if (projectProgress >= 50 && daysRemaining > 7) return { status: "On Track", color: "primary", icon: Info }

  if (projectProgress < 30 && daysRemaining <= 7) return { status: "Critical", color: "error", icon: Error }

  if (projectProgress < 70 && daysRemaining <= 7) return { status: "At Risk", color: "warning", icon: Warning }

  if (projectProgress >= 0) return { status: "On Track", color: "primary", icon: Info }

  return { status: "Needs Planning", color: "warning", icon: Warning }
}

const ProfessionalProjectDashboardEmp = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedSections, setExpandedSections] = useState({
    tasks: true,
    team: true,
    timeline: false,
  })

  const [dashboardData, setDashboardData] = useState({
    project: null,
    team: [],
    issues: [],
    stats: {
      totalTeamMembers: 0,
      totalIssues: 0,
      completedIssues: 0,
      taskCompletionPercentage: 0,
      projectProgress: 0,
      budget: 0,
      daysRemaining: 0,
      totalDays: 0,
      overallHealth: "Unknown",
    },
  })

  const fetchDashboardData = async () => {
    if (!projectId) {
      setError("No project ID provided")
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const projectResponse = await fetch(`${API_BASE_URL}/api/dash/dashboard-emp`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      let projectData = null
      if (projectResponse.ok) {
        const projectResult = await projectResponse.json()
        const projects = Array.isArray(projectResult)
          ? projectResult
          : projectResult.projects || projectResult.data || []

        projectData = projects.find((p) => p.id === Number.parseInt(projectId) || p.id === projectId)
      }

      const issuesResponse = await fetch(`${API_BASE_URL}/api/issues`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      let issuesData = []
      if (issuesResponse.ok) {
        const issuesResult = await issuesResponse.json()
        const allIssues = Array.isArray(issuesResult) ? issuesResult : issuesResult.issues || issuesResult.data || []

        issuesData = allIssues.filter(
          (issue) =>
            issue.project_id === Number.parseInt(projectId) ||
            issue.projectId === Number.parseInt(projectId) ||
            issue.project === Number.parseInt(projectId),
        )
      }

      let teamData = []
      try {
        const teamResponse = await fetch(`${API_BASE_URL}/api/addteam/project/${projectId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        })

        if (teamResponse.ok) {
          const teamResult = await teamResponse.json()
          teamData = Array.isArray(teamResult)
            ? teamResult
            : teamResult.data || teamResult.teamMembers || teamResult.members || []
        }
      } catch (teamError) {
        console.warn("Team data not available:", teamError)
      }

      if (!projectData) {
        throw new Error("Project not found")
      }

      const totalTeamMembers = teamData.length
      const totalIssues = issuesData.length

      const completedIssues = issuesData.filter((issue) => {
        const status = String(issue.status || "").toLowerCase()
        return status === "completed" || status === "done" || status === "closed" || status === "3"
      }).length

      const taskCompletionPercentage = totalIssues > 0 ? Math.round((completedIssues / totalIssues) * 100) : 0

      const projectProgress =
        projectData.completionPercentage || projectData.completion || projectData.progress || taskCompletionPercentage

      const budget = projectData.budget || 0
      const daysRemaining = calculateDaysRemaining(projectData.endDate || projectData.end_date || projectData.dueDate)

      let totalDays = 0
      if (projectData.startDate && projectData.endDate) {
        const start = new Date(projectData.startDate)
        const end = new Date(projectData.endDate)
        totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24))
      }

      const healthInfo = getHealthStatus(projectProgress, taskCompletionPercentage, daysRemaining)

      setDashboardData({
        project: projectData,
        team: teamData,
        issues: issuesData,
        stats: {
          totalTeamMembers,
          totalIssues,
          completedIssues,
          taskCompletionPercentage,
          projectProgress: Math.round(projectProgress),
          budget,
          daysRemaining,
          totalDays,
          overallHealth: healthInfo.status,
          healthColor: healthInfo.color,
          healthIcon: healthInfo.icon,
        },
      })
    } catch (err) {
      //console.error("Dashboard fetch error:", err)
      setError(`Failed to load project dashboard: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardData()
  }, [projectId])

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const handleBackToDashboard = () => {
    navigate("/dashboard")
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, bgcolor: "#ffffff", minHeight: "100vh" }}>
        <Skeleton variant="text" height={80} width="60%" sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={`card-${i}`}>
              <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3, bgcolor: "#ffffff", minHeight: "100vh" }}>
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={fetchDashboardData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={handleBackToDashboard} variant="outlined">
          Back to Dashboard
        </Button>
      </Box>
    )
  }

  const { project, team, issues, stats } = dashboardData

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 2, sm: 3, md: 4 },
        bgcolor: "#ffffff",
        minHeight: "100vh",
        "& .MuiCard-root": {
          boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04)",
          borderRadius: 3,
          border: "1px solid #e8e8e8",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 2px 6px rgba(0,0,0,0.06), 0 8px 20px rgba(0,0,0,0.06)",
          },
        },
      }}
    >
      {/* Professional Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          bgcolor: "#ffffff",
          borderRadius: 3,
          border: "1px solid #e8e8e8",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: "auto" } }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "#1a1a1a",
                  fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2.125rem" },
                  letterSpacing: "-0.02em",
                }}
              >
                {project?.name || project?.projectName || "Project Dashboard"}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
              {project?.projectKey && (
                <Chip
                  label={project.projectKey}
                  sx={{
                    bgcolor: "#f5f5f5",
                    color: "#333",
                    fontWeight: 600,
                    fontSize: "0.813rem",
                    height: 28,
                    border: "1px solid #e0e0e0",
                  }}
                />
              )}
              <Chip
                icon={React.createElement(stats.healthIcon, { sx: { color: "inherit !important", fontSize: 18 } })}
                label={stats.overallHealth}
                color={stats.healthColor}
                sx={{ 
                  fontWeight: 600, 
                  fontSize: "0.813rem",
                  height: 28,
                }}
              />
              {stats.daysRemaining !== null && (
                <Chip
                  icon={<Schedule sx={{ color: "inherit !important", fontSize: 18 }} />}
                  label={
                    stats.daysRemaining > 0
                      ? `${stats.daysRemaining} days left`
                      : stats.daysRemaining < 0
                        ? "Overdue"
                        : "Due today"
                  }
                  color={stats.daysRemaining > 0 ? "success" : "error"}
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: "0.813rem",
                    height: 28,
                  }}
                />
              )}
            </Stack>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Refresh Dashboard">
              <IconButton
                onClick={fetchDashboardData}
                sx={{
                  color: "#666",
                  bgcolor: "#f8f9fa",
                  border: "1px solid #e8e8e8",
                  "&:hover": { 
                    bgcolor: "#f0f0f0",
                    borderColor: "#d0d0d0",
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Key Performance Metrics */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0}>
            <CardContent sx={{ textAlign: "center", py: 3.5 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <Timeline sx={{ fontSize: 28, color: theme.palette.primary.main }} />
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: "#1a1a1a", mb: 0.5, fontSize: "2.25rem" }}>
                {stats.projectProgress}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 2 }}>
                Project Progress
              </Typography>
              <LinearProgress
                variant="determinate"
                value={stats.projectProgress}
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                  }
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0}>
            <CardContent sx={{ textAlign: "center", py: 3.5 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <Group sx={{ fontSize: 28, color: theme.palette.success.main }} />
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: "#1a1a1a", mb: 0.5, fontSize: "2.25rem" }}>
                {stats.totalTeamMembers}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Team Members
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0}>
            <CardContent sx={{ textAlign: "center", py: 3.5 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <Assignment sx={{ fontSize: 28, color: theme.palette.info.main }} />
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: "#1a1a1a", mb: 0.5, fontSize: "2.25rem" }}>
                {stats.taskCompletionPercentage}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                Task Completion
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.completedIssues} of {stats.totalIssues} completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

      </Grid> */}

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Project Overview */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ height: "100%" }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.125rem", color: "#1a1a1a" }}>
                  Project Overview
                </Typography>
              }
              subheader={
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Key project information and timeline
                </Typography>
              }
              avatar={
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AccountTree sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                </Box>
              }
              sx={{ pb: 1 }}
            />
            <Divider sx={{ borderColor: "#f0f0f0" }} />
            <CardContent sx={{ pt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                        Timeline
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                        <CalendarToday sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatDate(project?.startDate)} - {formatDate(project?.endDate || project?.dueDate)}
                        </Typography>
                      </Box>
                      {stats.totalDays > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                          Total duration: {stats.totalDays} days
                        </Typography>
                      )}
                    </Box>
 <Box>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                        Project Status
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Status: {stats.overallHealth}
                              </Typography>
                              <Typography variant="body2">
                                {stats.overallHealth === "Excellent" && "Project is 90% or more complete."}
                                {stats.overallHealth === "Good" &&
                                  "Project is 70% or more complete with sufficient time remaining."}
                                {stats.overallHealth === "On Track" &&
                                  "Project is progressing as expected with adequate time remaining."}
                                {stats.overallHealth === "At Risk" &&
                                  "Project has less than 70% completion with 7 or fewer days remaining."}
                                {stats.overallHealth === "Critical" &&
                                  "Project has less than 30% completion with 7 or fewer days remaining."}
                                {stats.overallHealth === "Overdue" && "Project has passed its due date."}
                                {stats.overallHealth === "Needs Planning" &&
                                  "Project has low progress but sufficient time remaining."}
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" component="div">
                                  • Progress: {stats.projectProgress}%
                                </Typography>
                                <Typography variant="caption" component="div">
                                  • Days remaining: {stats.daysRemaining !== null ? stats.daysRemaining : "Not set"}
                                </Typography>
                                <Typography variant="caption" component="div">
                                  • Tasks completed: {stats.completedIssues} of {stats.totalIssues}
                                </Typography>
                              </Box>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <Chip
                            icon={React.createElement(stats.healthIcon, { sx: { fontSize: 18 } })}
                            label={stats.overallHealth}
                            color={stats.healthColor}
                            variant="filled"
                            sx={{ fontWeight: 600, cursor: "help", height: 32 }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>

                 
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack spacing={3}>
                   
                    <Box>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.75rem", mb: 1.5, display: "block" }}>
                        Key Metrics
                      </Typography>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="body2" color="text.secondary">Project Progress</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
                            {stats.projectProgress}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="body2" color="text.secondary">Task Completion</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
                            {stats.taskCompletionPercentage}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="body2" color="text.secondary">Active Tasks</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
                            {stats.totalIssues - stats.completedIssues}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="body2" color="text.secondary">Team Size</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
                            {stats.totalTeamMembers}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Project Health Summary */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ height: "100%" }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.125rem", color: "#1a1a1a" }}>
                  Project Health
                </Typography>
              }
              subheader={
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Overall status and key metrics
                </Typography>
              }
              avatar={
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Assessment sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                </Box>
              }
              sx={{ pb: 1 }}
            />
            <Divider sx={{ borderColor: "#f0f0f0" }} />
            <CardContent sx={{ pt: 3 }}>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Status Criteria:
                      </Typography>
                      <Typography variant="caption" component="div">
                        • Excellent: 90% or more completion
                      </Typography>
                      <Typography variant="caption" component="div">
                        • Good: 70% or more completion
                      </Typography>
                      <Typography variant="caption" component="div">
                        • On Track: 50% or more completion with more than 7 days remaining
                      </Typography>
                      <Typography variant="caption" component="div">
                        • At Risk: Less than 70% completion with 7 or fewer days remaining
                      </Typography>
                      <Typography variant="caption" component="div">
                        • Critical: Less than 30% completion with 7 or fewer days remaining
                      </Typography>
                      <Typography variant="caption" component="div">
                        • Overdue: Past due date
                      </Typography>
                      <Typography variant="caption" component="div">
                        • Needs Planning: Low progress but sufficient time remaining
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Box sx={{ cursor: "help" }}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette[stats.healthColor]?.main || "#666", 0.08),
                        color: theme.palette[stats.healthColor]?.main || "#666",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      {React.createElement(stats.healthIcon, { sx: { fontSize: 36 } })}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1a1a" }} gutterBottom>
                      {stats.overallHealth}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>

              <Stack spacing={2}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 2.5, 
                    bgcolor: "#fafafa",
                    border: "1px solid #f0f0f0",
                    borderRadius: 2,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Speed sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
                        Velocity
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stats.completedIssues} tasks completed
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 2.5, 
                    bgcolor: "#fafafa",
                    border: "1px solid #f0f0f0",
                    borderRadius: 2,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Group sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
                        Team
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stats.totalTeamMembers} active members
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 2.5, 
                    bgcolor: "#fafafa",
                    border: "1px solid #f0f0f0",
                    borderRadius: 2,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.warning.main, 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Schedule sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
                        Timeline
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stats.daysRemaining > 0
                          ? `${stats.daysRemaining} days remaining`
                          : stats.daysRemaining < 0
                            ? "Project is overdue"
                            : "Due today"}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Team Members */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ height: "500px" }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.125rem", color: "#1a1a1a" }}>
                  Team Members ({team.length})
                </Typography>
              }
              subheader={
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Active project contributors
                </Typography>
              }
              avatar={
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Group sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                </Box>
              }
              action={
                <IconButton onClick={() => toggleSection("team")} size="small">
                  {expandedSections.team ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              }
              sx={{ pb: 1 }}
            />
            <Divider sx={{ borderColor: "#f0f0f0" }} />
            <Collapse in={expandedSections.team} timeout="auto" unmountOnExit>
              <List sx={{ maxHeight: 420, overflow: "auto", px: 1 }}>
                {team.length > 0 ? (
                  team.map((member, index) => {
                    const memberName =
                      member.name || member.employee_name || member.userName || `Team Member ${index + 1}`
                    const memberRole = member.role || member.position || member.designation || "Developer"
                    const memberEmail = member.email || member.user_email || ""
                    const memberAllocation = member.allocation || member.allocated_hours || member.workload || null
                    
                    return (
                      <ListItem 
                        key={member.id || member.employee_id || index} 
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          border: "1px solid #f0f0f0",
                          bgcolor: "#fafafa",
                          "&:hover": {
                            bgcolor: "#f5f5f5",
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Avatar 
                            sx={{ 
                              bgcolor: theme.palette.primary.main,
                              width: 40,
                              height: 40,
                              fontWeight: 600,
                            }}
                          >
                            {memberName.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
                              {memberName}
                            </Typography>
                          }
                          secondary={
                            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                              <Chip
                                label={memberRole}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  fontSize: "0.7rem", 
                                  alignSelf: "flex-start",
                                  height: 22,
                                  borderColor: "#e0e0e0",
                                }}
                              />
                              {memberAllocation && (
                                <Chip
                                  label={`${memberAllocation}% allocated`}
                                  size="small"
                                  sx={{ 
                                    fontSize: "0.7rem", 
                                    alignSelf: "flex-start",
                                    height: 22,
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                  }}
                                />
                              )}
                            </Stack>
                          }
                        />
                        {memberEmail && (
                          <ListItemSecondaryAction>
                            <Tooltip title={memberEmail}>
                              <IconButton 
                                size="small"
                                sx={{
                                  bgcolor: "#ffffff",
                                  border: "1px solid #e8e8e8",
                                  "&:hover": {
                                    bgcolor: "#f5f5f5",
                                  }
                                }}
                              >
                                <Person fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                    )
                  })
                ) : (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                          No team members assigned
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Collapse>
          </Card>
        </Grid>

        {/* Recent Tasks/Issues */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ height: "500px" }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.125rem", color: "#1a1a1a" }}>
                  Recent Tasks ({issues.length})
                </Typography>
              }
              subheader={
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Latest project activities
                </Typography>
              }
              avatar={
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Assignment sx={{ color: theme.palette.info.main, fontSize: 20 }} />
                </Box>
              }
              action={
                <IconButton onClick={() => toggleSection("tasks")} size="small">
                  {expandedSections.tasks ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              }
              sx={{ pb: 1 }}
            />
            <Divider sx={{ borderColor: "#f0f0f0" }} />
            <Collapse in={expandedSections.tasks} timeout="auto" unmountOnExit>
              <List sx={{ maxHeight: 420, overflow: "auto", px: 1 }}>
                {issues.length > 0 ? (
                  issues.slice(0, 10).map((task, index) => {
                    const taskTitle = task.title || task.name || task.summary || `Task ${index + 1}`
                    const taskStatus = task.status || "Open"
                    const taskPriority = task.priority || "Medium"

                    const getAssigneeName = () => {
                      if (typeof task.assignee === "string" && !task.assignee.match(/^\d+$/)) {
                        return task.assignee
                      }

                      if (task.assignee && task.assignee.name) {
                        return task.assignee.name
                      }

                      if (task.assigned_to && task.assigned_to.name) {
                        return task.assigned_to.name
                      }

                      if (task.assignee_name) {
                        return task.assignee_name
                      }

                      if (task.assigned_to_name) {
                        return task.assigned_to_name
                      }

                      if ((task.assignee || task.assigned_to) && team.length > 0) {
                        const assigneeId = task.assignee || task.assigned_to
                        const teamMember = team.find(
                          (member) => member.id === assigneeId || member.employee_id === assigneeId,
                        )
                        if (teamMember) {
                          return teamMember.name || teamMember.employee_name || teamMember.userName || `Team Member`
                        }
                      }

                      return "Unassigned"
                    }

                    const taskAssignee = getAssigneeName()

                    return (
                      <ListItem 
                        key={task.id || index}
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          border: "1px solid #f0f0f0",
                          bgcolor: "#fafafa",
                          "&:hover": {
                            bgcolor: "#f5f5f5",
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 1.5,
                              bgcolor: alpha(getPriorityColor(taskPriority), 0.08),
                              color: getPriorityColor(taskPriority),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <TaskAlt fontSize="small" />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "#1a1a1a" }}>
                              {taskTitle}
                            </Typography>
                          }
                          secondary={
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                              <Chip
                                label={getStatusDisplayName(taskStatus)}
                                size="small"
                                color={getStatusColor(taskStatus)}
                                variant="filled"
                                sx={{ fontSize: "0.7rem", height: 22, fontWeight: 500 }}
                              />
                              <Chip
                                label={getPriorityDisplayName(taskPriority)}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: "0.7rem",
                                  height: 22,
                                  borderColor: getPriorityColor(taskPriority),
                                  color: getPriorityColor(taskPriority),
                                  fontWeight: 500,
                                }}
                              />
                              {taskAssignee !== "Unassigned" && (
                                <Chip
                                  label={taskAssignee}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: "0.7rem", height: 22, borderColor: "#e0e0e0" }}
                                />
                              )}
                            </Stack>
                          }
                        />
                      </ListItem>
                    )
                  })
                ) : (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                          No tasks found for this project
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
              {issues.length > 10 && (
                <CardActions sx={{ justifyContent: "center", borderTop: "1px solid #f0f0f0" }}>
                  <Button size="small" sx={{ fontWeight: 600 }}>
                    View All Tasks ({issues.length})
                  </Button>
                </CardActions>
              )}
            </Collapse>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default ProfessionalProjectDashboardEmp;